import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Check, AlertCircle, Loader2, ShieldCheck } from "lucide-react";
import axios from "axios";
import LegalLayout from "../components/legal/LegalLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DeleteAccount = () => {
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle, loading, success, error, not_found
  const [errorMessage, setErrorMessage] = useState("");

  const handleDelete = async (e) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      setErrorMessage("Please enter a valid email address.");
      setStatus("error");
      return;
    }

    if (email.toLowerCase().trim() !== confirmEmail.toLowerCase().trim()) {
      setErrorMessage("Email addresses do not match. Please re-enter to confirm.");
      setStatus("error");
      return;
    }

    setStatus("loading");
    try {
      const response = await axios.post(`${BACKEND_URL}/api/delete-account`, {
        email: email.toLowerCase().trim()
      });

      if (response.data.success) {
        setStatus("success");
      } else {
        setErrorMessage(response.data.message || "Something went wrong.");
        setStatus("error");
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setStatus("not_found");
      } else {
        setErrorMessage(err.response?.data?.detail || "Something went wrong. Please try again or email support@thryvin.app.");
        setStatus("error");
      }
    }
  };

  return (
    <LegalLayout title="Delete Your Account" testId="delete-account-content">
      <h1>Delete Your Thryvin' Account</h1>
      <p className="last-updated">Last updated: 19 October 2026</p>

      <p className="intro">
        You can delete your Thryvin' account and the personal data linked to it at any time.
      </p>

      {/* Option 1 */}
      <section>
        <h2>Option 1: in the app (fastest)</h2>
        <ol className="delete-steps">
          <li>Open the Thryvin' app</li>
          <li>Go to <strong>You → Settings → Delete account</strong></li>
          <li>Confirm twice</li>
        </ol>
        <p>We delete your account and data from our live systems, and send you one email to confirm. A few records are kept for a limited time (see "What we may keep, and why" below).</p>
      </section>

      {/* Option 2 */}
      <section>
        <h2>Option 2: by email (if you no longer have the app)</h2>
        <p>
          Email{" "}
          <a href="mailto:support@thryvin.app?subject=Delete%20my%20account" className="inline-link">
            <strong>support@thryvin.app</strong>
          </a>{" "}
          from the email address on your Thryvin' account, with the subject line <strong>"Delete my account"</strong>. We'll check it's you, delete the account and email you when it's done, within 30 days.
        </p>
      </section>

      {/* Option 3 — Online form */}
      <section>
        <h2>Option 3: delete your waitlist data</h2>
        <p>If you signed up to the Thryvin' waitlist and want your data removed, use the form below.</p>

        <div className="delete-form-card" data-testid="delete-form-card">
          {status === "success" ? (
            <div className="delete-result success" data-testid="delete-success">
              <div className="delete-result-icon success-icon">
                <Check size={32} />
              </div>
              <h3>Account data deleted</h3>
              <p>Your email and all associated waitlist data have been permanently removed from our systems.</p>
              <p className="delete-result-note">If you also have an account in the Thryvin' app, please use Option 1 or Option 2 above to delete that separately.</p>
            </div>
          ) : status === "not_found" ? (
            <div className="delete-result not-found" data-testid="delete-not-found">
              <div className="delete-result-icon not-found-icon">
                <AlertCircle size={32} />
              </div>
              <h3>Email not found</h3>
              <p>We couldn't find that email address in our waitlist. It may have already been deleted, or it was never registered.</p>
              <p>If you believe this is an error, please contact <a href="mailto:support@thryvin.app" className="inline-link">support@thryvin.app</a> for help.</p>
              <button className="delete-try-again-btn" onClick={() => { setStatus("idle"); setEmail(""); setConfirmEmail(""); }}>
                Try another email
              </button>
            </div>
          ) : (
            <>
              <div className="delete-form-header">
                <ShieldCheck size={24} />
                <div>
                  <h3>Confirm your identity</h3>
                  <p>Enter the email address you signed up with. Type it twice to confirm.</p>
                </div>
              </div>

              <form onSubmit={handleDelete} className="delete-form" data-testid="delete-form">
                <div className="delete-form-field">
                  <label htmlFor="delete-email">Email address</label>
                  <input
                    id="delete-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
                    disabled={status === "loading"}
                    data-testid="delete-email-input"
                  />
                </div>

                <div className="delete-form-field">
                  <label htmlFor="confirm-email">Confirm email address</label>
                  <input
                    id="confirm-email"
                    type="email"
                    placeholder="Type your email again"
                    value={confirmEmail}
                    onChange={(e) => { setConfirmEmail(e.target.value); if (status === "error") setStatus("idle"); }}
                    disabled={status === "loading"}
                    data-testid="delete-confirm-email-input"
                  />
                </div>

                {status === "error" && (
                  <p className="delete-form-error" data-testid="delete-error">{errorMessage}</p>
                )}

                <button
                  type="submit"
                  className="delete-submit-btn"
                  disabled={status === "loading" || !email || !confirmEmail}
                  data-testid="delete-submit-btn"
                >
                  {status === "loading" ? (
                    <><Loader2 size={18} className="spinner" /> Processing...</>
                  ) : (
                    <><Trash2 size={18} /> Delete my data</>
                  )}
                </button>
              </form>

              <p className="delete-form-disclaimer">This action is permanent and cannot be undone.</p>
            </>
          )}
        </div>
      </section>

      {/* Subscriptions */}
      <section>
        <h2>Subscriptions</h2>
        <p>Deleting your account doesn't cancel a Thryvin' Pro subscription. Cancel it in your Apple App Store or Google Play account so you aren't charged again.</p>
      </section>

      {/* What gets deleted */}
      <section>
        <h2>What gets deleted</h2>
        <ul>
          <li>Your account and profile (name, email, date of birth, body details, goals and preferences)</li>
          <li>Injuries, allergies and diet choices, and pregnancy or new-baby details</li>
          <li>Your training plans, workouts, personal bests and check-ins, including progress photos</li>
          <li>Food logs, meal plans, saved meals and recipes, shopping lists and nutrition settings</li>
          <li>Coach chats and what your coach has learned about you</li>
          <li>Data from connected apps (Apple Health, Health Connect, Strava) and the links to them</li>
          <li>Your Community profile, posts, comments, reactions and photos</li>
        </ul>
      </section>

      {/* What we retain */}
      <section>
        <h2>What we may keep, and why</h2>
        <ul>
          <li><strong>Purchase and tax records</strong> (including subscription records linked to your account number): for as long as the law requires, usually 6 years. Payment details are held by Apple, Google and RevenueCat, not by us.</li>
          <li><strong>Error and crash reports</strong> linked to your account number: up to 30 days, then deleted.</li>
          <li><strong>Emails waiting to be sent:</strong> up to 30 days.</li>
          <li><strong>Reports you made about other people's content:</strong> kept without your name, so moderation decisions still stand.</li>
          <li><strong>Records of enforcement</strong> (warnings, suspensions and bans): for as long as needed to enforce them and handle appeals.</li>
          <li><strong>Suspected child sexual abuse material:</strong> kept for the authorities, as the law requires.</li>
          <li><strong>Backups:</strong> short-term restore copies are overwritten on their normal cycle.</li>
        </ul>
      </section>

      {/* Partial deletion */}
      <section>
        <h2>Deleting some data without closing your account</h2>
        <ul>
          <li><strong>Community:</strong> choose Leave Community, and tick "Also delete my Community data".</li>
          <li><strong>Allergies and diet choices:</strong> remove them in your nutrition settings.</li>
          <li><strong>AI permission:</strong> switch it off in You → Settings → Privacy &amp; AI.</li>
          <li><strong>A copy of your data:</strong> You → Settings → Privacy &amp; AI → Download my data.</li>
        </ul>
      </section>

      {/* Questions */}
      <section>
        <h2>Questions?</h2>
        <p>Email <a href="mailto:support@thryvin.app" className="inline-link">support@thryvin.app</a>. Full details are in our <Link to="/privacy" className="inline-link">Privacy Policy</Link>.</p>
      </section>
    </LegalLayout>
  );
};

export default DeleteAccount;
